import { useContext, useEffect, useState } from "react";
import { CurrentEmployeesType } from "types";
import { Button, Dialog } from "@justworkshr/milo-components";
import Alert from "uicomponents/Alert";
import styles from "./CurrentEmployees.module.css";
import { useCensusAndCurrentInformationContext } from "pages/CensusAndCurrentInformation/CensusAndCurrentInformation";
import {
  trackFormError,
  trackPageViewUserStep,
  trackSaveAndContinueUserStep,
} from "utils/analytics/trackEvent";
import { CURRENT_EMPLOYEES_FORM_TITLE } from "pages/CensusAndCurrentInformation/utils/constants";
import {
  STEPS_WITH_COBRA,
  STEPS_WITHOUT_COBRA,
} from "pages/CensusAndCurrentInformation/utils/constants";
import Error from "uicomponents/Error";
import Stepper from "uicomponents/Stepper";
import { errorMessageMap, getStepNumberFromURL } from "utils/helper";
import newstyles from "../../CensusAndCurrentInformation.module.css";
import { mapBEStepstoMatchFE } from "pages/CensusAndCurrentInformation/utils/helper";
import EmployeeTableUpsell from "uicomponents/EmployeeTableUpsell";
import uniqBy from "lodash/uniqBy";
import FooterButton from "uicomponents/FooterButton";
import {
  useUpdateStepMutation,
  EmployeeCensusSourceEnum,
} from "types/generated/operations";
import { TotalDependentContext } from "./TotalDependentContext";
interface ICurrentEmployeesUpsellProps {
  data: CurrentEmployeesType;
}

const CurrentEmployeesUpsell = (
  props: ICurrentEmployeesUpsellProps
): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(5, "current empoloyee information");
  }, []);

  const { data } = props;

  let employeeData = data?.employeeCensuses?.filter(
    (emp) => emp.source === EmployeeCensusSourceEnum.HiUpsell
  );

  if (employeeData?.length === 0) {
    employeeData = data?.employeeCensuses?.filter((emp) => !emp?.email);
  }

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const totalDependentContext = useContext(TotalDependentContext);

  const [
    isContinueWithoutAddingDependentModalOpen,
    setIsContinueWithoutAddingDependentModalOpen,
  ] = useState(false);

  const closeDialog = () => {
    setIsContinueWithoutAddingDependentModalOpen(false);
  };

  const hasCobra = data?.healthInsuranceInformation?.cobraUsage;

  const { stepperNextStep, previousStep, nextStep } =
    useCensusAndCurrentInformationContext();

  const [updateStep, { loading: updateStepLoading }] = useUpdateStepMutation();

  const applicationStep = data?.applications?.[0]?.step || 0;
  const currentStep = getStepNumberFromURL();
  const isInValidStep = applicationStep && currentStep > applicationStep;

  if (isInValidStep) {
    return <Error />;
  }

  const STEPS = hasCobra ? STEPS_WITH_COBRA : STEPS_WITHOUT_COBRA;

  const employeeWithUniqueDepartment = uniqBy(
    employeeData,
    "employeeInformation.department"
  )
    .filter((emp) => emp.isCompanyOwner === false)
    .map((emp) => emp.employeeInformation?.department)
    .sort();

  const owners = employeeData?.filter((emp) => emp.isCompanyOwner === true);

  const showModalAndContinue = () => {
    if (
      (totalDependentContext && totalDependentContext?.totalDependent >= 1) ||
      applicationStep > currentStep
    ) {
      handleNextStep();
    } else {
      setIsContinueWithoutAddingDependentModalOpen(true);
    }
  };

  const handleNextStep = async () => {
    if (isContinueWithoutAddingDependentModalOpen)
      setIsContinueWithoutAddingDependentModalOpen(false);
    try {
      const currentStep = getStepNumberFromURL();
      if (data?.applications?.[0]?.step === currentStep) {
        await updateStep({
          variables: {
            uuid: data?.applications?.[0].uuid,
            step: data?.applications?.[0]?.step + 1,
          },
        });
        trackSaveAndContinueUserStep(currentStep, "Current Employees upsell");
      }
      nextStep();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  return (
    <div className={newstyles.censusAndCurrentInformationformContainer}>
      <Stepper
        steps={STEPS}
        currentStep={mapBEStepstoMatchFE(getStepNumberFromURL())}
        status={mapBEStepstoMatchFE(data?.applications?.[0].step || 0) || 8}
        onClick={(stepNumber) => stepperNextStep(stepNumber)}
      />
      <div className={newstyles.censusAndCurrentInformationformDiv}>
        <div className={styles.title}>{CURRENT_EMPLOYEES_FORM_TITLE}</div>
        <div className={styles.descriptionUpsell}>
          We've automatically included all owners and current employees who are
          eligible for health insurance.
          <div className={styles.descriptionWithBold}>Next up:</div>
          <div>
            <ul>
              <li>
                Double check all the information is correct. To make any edits,
                go to the employee’s profile in Manage {">"} Employees.
              </li>
              <li>
                Add in all dependents for each person if those dependents are
                enrolled in your current plan or intend to enroll in a new plan.
              </li>
            </ul>
          </div>
          <div>
            All eligible employees must be included, even if they don’t intend
            to sign up for coverage.
          </div>
        </div>
        <div
          className={styles.upsellContainer}
          data-testid="employees-table-upsell"
        >
          <span
            className={styles.header}
          >{`${employeeData?.length} eligible company staff`}</span>
          {owners && owners.length > 0 && (
            <div className={styles.table}>
              <div className={styles.tableTitle}>{"Owners"}</div>
              {owners?.map((eachOwner: any, index: any) => (
                <EmployeeTableUpsell data={eachOwner} key={index} />
              ))}
            </div>
          )}
          {employeeWithUniqueDepartment?.map((item, index) => (
            <div className={styles.table} key={index}>
              <div className={styles.tableTitle}>{item || "Department"}</div>
              {employeeData
                ?.filter(
                  (eachitem) =>
                    eachitem.employeeInformation?.department === item &&
                    !eachitem.isCompanyOwner
                )
                .map((eachEmployee: any, index: any) => (
                  <EmployeeTableUpsell data={eachEmployee} key={index} />
                ))}
            </div>
          ))}
        </div>
        <FooterButton
          nextButtonText={updateStepLoading ? "Loading" : "Save & continue"}
          backButtonText={"Back"}
          handleBackClick={previousStep}
          handleNextClick={showModalAndContinue}
          disabled={updateStepLoading}
        />
        {isError && <Alert message={errorMessage} />}
        <Dialog
          isOpen={isContinueWithoutAddingDependentModalOpen}
          onClose={closeDialog}
          title="Want to add any dependents?"
          primaryButton={
            <Button onClick={handleNextStep} variant="filled">
              Yes, save & continue
            </Button>
          }
          secondaryButton={
            <Button onClick={closeDialog} variant="ghost">
              Go back
            </Button>
          }
        >
          <p>
            Are you sure you want to continue without adding any dependents for
            your employees?
          </p>
        </Dialog>
      </div>
    </div>
  );
};

export default CurrentEmployeesUpsell;
